/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { Truck, Plus, Trash2, Search, Calculator } from "lucide-react";
import ColumnChooser from "@/components/ColumnChooser";
import { useColumnPrefs } from "@/hooks/useColumnPrefs";
import { useAppSettings } from "@/hooks/useAppSettings";

export default function DeliveryZonesPage() {
  const toast = useToast();
  const settings = useAppSettings();
  const defaultPerPallet = settings?.Default_Cost_Per_Pallet ?? 41;
  const [zones, setZones] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ Zone_Name: "", Min_Postcode: "", Max_Postcode: "", Cost_AUD: "", Cost_Per_Pallet: "" });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = async () => {
    try { const { data } = await api.get("/delivery-zones"); setZones(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const add = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      await api.post("/delivery-zones", {
        Zone_Name: form.Zone_Name,
        Min_Postcode: Number(form.Min_Postcode),
        Max_Postcode: Number(form.Max_Postcode),
        Cost_AUD: Number(form.Cost_AUD),
        Cost_Per_Pallet: Number(form.Cost_Per_Pallet || defaultPerPallet || 0),
      });
      setForm({ Zone_Name: "", Min_Postcode: "", Max_Postcode: "", Cost_AUD: "", Cost_Per_Pallet: "" });
      load();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const startEdit = (z) => {
    setEditingId(z.id);
    setEditForm({
      Zone_Name: z.Zone_Name,
      Min_Postcode: z.Min_Postcode,
      Max_Postcode: z.Max_Postcode,
      Cost_AUD: z.Cost_AUD,
      Cost_Per_Pallet: z.Cost_Per_Pallet || 0,
    });
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/delivery-zones/${editingId}`, editForm);
      setEditingId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this zone?")) return;
    try { await api.delete(`/delivery-zones/${id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const filtered = zones.filter((z) =>
    !q || z.Zone_Name.toLowerCase().includes(q.toLowerCase())
    || String(z.Min_Postcode).includes(q) || String(z.Max_Postcode).includes(q)
  );

  const allColumns = [
    { key: "Zone_Name", label: "Zone Name", cell: (z) => <span className="font-semibold">{z.Zone_Name}</span> },
    { key: "Postcode_Range", label: "Postcode Range", align: "center", cell: (z) => <span className="font-mono">{z.Min_Postcode} – {z.Max_Postcode}</span> },
    { key: "Cost_AUD", label: "Base / 1st Pallet (AUD)", align: "center", cell: (z) => <span className="font-mono">${(z.Cost_AUD || 0).toFixed(2)}</span> },
    { key: "Cost_Per_Pallet", label: "Each Extra Pallet (AUD)", align: "center", cell: (z) => <span className="font-mono">{z.Cost_Per_Pallet ? `$${z.Cost_Per_Pallet.toFixed(2)}` : "—"}</span> },
    {
      key: "_actions", label: "Actions", locked: true, align: "right",
      cell: (z) => (
        <>
          <button onClick={() => startEdit(z)} data-testid={`zone-edit-${z.id}`} className="text-xs px-2 py-1 rounded-md border border-zinc-200 hover:border-brand-500 hover:text-brand-700 mr-1">Edit</button>
          <button onClick={() => remove(z.id)} className="text-zinc-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
        </>
      ),
    },
  ];

  const { visibleColumns, orderedColumns, isVisible, toggleColumn, moveColumn, reset } =
    useColumnPrefs("delivery_zones", allColumns);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Delivery Zones</h1>
        <p className="text-xs sm:text-sm text-zinc-500 mt-1">Postcode-based delivery pricing used by the reseller portal quote builder.</p>
      </div>

      <div className="es-card">
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2">
          <Truck className="w-4 h-4 text-brand-600" />
          <h2 className="font-display font-semibold">Add a zone</h2>
          <span className="ml-auto text-xs text-zinc-500">{zones.length} zone{zones.length === 1 ? "" : "s"}</span>
        </div>
        <form onSubmit={add} className="px-5 py-4 grid grid-cols-1 sm:grid-cols-6 gap-3">
          <div className="sm:col-span-2"><label className="es-label">Zone Name</label>
            <input data-testid="zone-name" required placeholder="e.g. Gold Coast" value={form.Zone_Name} onChange={(e) => u("Zone_Name", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">Min Postcode</label>
            <input data-testid="zone-min" required type="number" placeholder="4000" value={form.Min_Postcode} onChange={(e) => u("Min_Postcode", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">Max Postcode</label>
            <input data-testid="zone-max" required type="number" placeholder="4099" value={form.Max_Postcode} onChange={(e) => u("Max_Postcode", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">Base / 1st Pallet (AUD)</label>
            <input data-testid="zone-cost" required type="number" step="0.01" placeholder="50.00" value={form.Cost_AUD} onChange={(e) => u("Cost_AUD", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">Each Extra Pallet (AUD)</label>
            <input data-testid="zone-per-pallet" type="number" step="0.01" placeholder={String(defaultPerPallet)} value={form.Cost_Per_Pallet} onChange={(e) => u("Cost_Per_Pallet", e.target.value)} className="es-input" /></div>
          {err && <div className="sm:col-span-6 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}
          <div className="sm:col-span-6">
            <button data-testid="zone-add" disabled={saving} className="es-btn-primary"><Plus className="w-4 h-4" /> {saving ? "Adding…" : "Add Zone"}</button>
          </div>
        </form>
      </div>

      <div className="es-card">
        <div className="px-3 sm:px-5 py-3 border-b border-zinc-100 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[160px] max-w-sm">
            <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search zones…" className="es-input pl-9 w-full" />
          </div>
          <ColumnChooser
            tableLabel="delivery_zones"
            orderedColumns={orderedColumns}
            isVisible={isVisible}
            toggleColumn={toggleColumn}
            moveColumn={moveColumn}
            reset={reset}
          />
        </div>
        <div className="overflow-x-auto">
        <table className="es-table min-w-[640px]">
          <thead>
            <tr>
              {visibleColumns.map((c) => (
                <th key={c.key} className={[c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "", c.width || ""].join(" ")}>
                  {c.header ? c.header() : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={visibleColumns.length} className="text-center py-10 text-sm text-zinc-400">No zones {q && "match"}.</td></tr>}
            {filtered.map((z) => {
              const editing = editingId === z.id;
              const eu = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));
              if (editing) {
                // While editing, show a single full-width edit row that uses
                // the same number of cells as the visible columns so the
                // layout doesn't collapse. The edit form ignores the chooser.
                return (
                  <tr key={z.id} data-testid={`zone-row-${z.id}`}>
                    <td colSpan={visibleColumns.length}>
                      <div className="flex items-center gap-2 flex-wrap py-1">
                        <input value={editForm.Zone_Name} onChange={(e) => eu("Zone_Name", e.target.value)} className="es-input w-40" placeholder="Zone name" />
                        <input type="number" value={editForm.Min_Postcode} onChange={(e) => eu("Min_Postcode", e.target.value)} className="es-input w-24 font-mono" placeholder="Min" />
                        <span>–</span>
                        <input type="number" value={editForm.Max_Postcode} onChange={(e) => eu("Max_Postcode", e.target.value)} className="es-input w-24 font-mono" placeholder="Max" />
                        <input type="number" step="0.01" value={editForm.Cost_AUD} onChange={(e) => eu("Cost_AUD", e.target.value)} className="es-input w-28 text-right font-mono" placeholder="Base $" />
                        <input type="number" step="0.01" value={editForm.Cost_Per_Pallet} onChange={(e) => eu("Cost_Per_Pallet", e.target.value)} className="es-input w-28 text-right font-mono" placeholder="Pallet $" />
                        <button onClick={saveEdit} data-testid={`zone-save-${z.id}`} className="text-xs px-2 py-1 rounded-md bg-brand-600 text-white hover:bg-brand-700">Save</button>
                        <button onClick={() => setEditingId(null)} className="text-xs px-2 py-1 rounded-md border border-zinc-200">Cancel</button>
                      </div>
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={z.id} data-testid={`zone-row-${z.id}`}>
                  {visibleColumns.map((c) => (
                    <td key={c.key} className={c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : ""}>{c.cell(z)}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Delivery cost calculator — mirrors the portal quote builder so admins
          can sanity-check a postcode without leaving Settings. Live formula:
          base + per_pallet × max(0, pallets - 1). */}
      <DeliveryCalculator />
    </div>
  );
}

function DeliveryCalculator() {
  const [postcode, setPostcode] = useState("");
  const [pallets, setPallets] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const calc = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr(""); setResult(null);
    const pc = Number(postcode);
    const p = Number(pallets);
    if (!Number.isFinite(pc) || pc < 1000 || pc > 9999) {
      setErr("Postcode must be a 4-digit number."); setBusy(false); return;
    }
    if (!Number.isInteger(p) || p < 0) {
      setErr("Pallets must be a whole number ≥ 0."); setBusy(false); return;
    }
    try {
      const { data } = await api.get("/delivery-zones/lookup", { params: { postcode: pc, pallets: p } });
      setResult(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const reset = () => { setPostcode(""); setPallets(""); setResult(null); setErr(""); };
  const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

  return (
    <div className="es-card" data-testid="delivery-calculator">
      <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2">
        <Calculator className="w-4 h-4 text-brand-600" />
        <h2 className="font-display font-semibold">Delivery cost calculator</h2>
        <span className="ml-auto text-xs text-zinc-500">Test a postcode + pallet count against your zones</span>
      </div>
      <form onSubmit={calc} className="px-5 py-4 grid grid-cols-1 sm:grid-cols-[160px_160px_auto] gap-3 items-end">
        <div>
          <label className="es-label" htmlFor="calc-postcode">Postcode</label>
          <input
            id="calc-postcode"
            data-testid="calc-postcode"
            type="number"
            inputMode="numeric"
            min="1000" max="9999"
            placeholder="4000"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            className="es-input font-mono"
          />
        </div>
        <div>
          <label className="es-label" htmlFor="calc-pallets">Pallets</label>
          <input
            id="calc-pallets"
            data-testid="calc-pallets"
            type="number"
            inputMode="numeric"
            min="0"
            placeholder="1"
            value={pallets}
            onChange={(e) => setPallets(e.target.value)}
            className="es-input font-mono"
          />
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="calc-submit" disabled={busy} className="es-btn-primary">
            <Calculator className="w-4 h-4" /> {busy ? "Calculating…" : "Calculate"}
          </button>
          {(result || err) && (
            <button type="button" onClick={reset} data-testid="calc-reset" className="es-btn-secondary">Clear</button>
          )}
        </div>
      </form>

      {err && (
        <div className="mx-5 mb-4 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg" data-testid="calc-error">{err}</div>
      )}

      {result && !err && (
        <div className="mx-5 mb-5" data-testid="calc-result">
          {result.matched ? (
            <div className="border border-emerald-200 bg-emerald-50/60 rounded-lg p-4">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-mono font-semibold">Match found</div>
                  <div className="font-display text-lg font-bold text-zinc-900 mt-0.5">{result.zone?.Zone_Name}</div>
                  <div className="text-xs font-mono text-zinc-500 mt-0.5">postcodes {result.zone?.Min_Postcode}–{result.zone?.Max_Postcode}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono font-semibold">Total delivery</div>
                  <div className="font-display text-3xl font-bold text-emerald-700 tabular-nums" data-testid="calc-result-total">{fmt(result.cost)}</div>
                </div>
              </div>
              {result.pallets > 0 && (
                <div className="mt-3 pt-3 border-t border-emerald-200/70 text-xs text-zinc-600 font-mono">
                  <div className="flex justify-between"><span>Base / first pallet</span><span>{fmt(result.base_cost)}</span></div>
                  {result.extra_pallets > 0 && (
                    <div className="flex justify-between mt-1">
                      <span>{result.extra_pallets} extra pallet{result.extra_pallets === 1 ? "" : "s"} × {fmt(result.per_pallet_cost)}</span>
                      <span>{fmt(result.per_pallet_cost * result.extra_pallets)}</span>
                    </div>
                  )}
                </div>
              )}
              {result.pallets === 0 && (
                <div className="mt-2 text-xs text-zinc-500 italic">No pallets entered — delivery not charged.</div>
              )}
            </div>
          ) : (
            <div className="border border-amber-200 bg-amber-50/60 rounded-lg p-4 flex items-start gap-3">
              <span className="w-2 h-2 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
              <div>
                <div className="font-semibold text-zinc-900">No zone covers postcode {postcode}.</div>
                <div className="text-xs text-zinc-600 mt-1">Add a zone above whose range includes this postcode, or leave delivery as a manual quote.</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
